import { MapDrawingConfig, drawSelectedRect } from "../canvas-drawing";
import {
  Backdrop,
  DataVisualization,
  GPSSource,
} from "PosterEditor/model/poster-project";
import { Point, Rect } from "PosterEditor/model/poster-types";
import { MapElement, DragHandle } from "../canvas-drawing";
import { pointIsInsideRect } from "../point-utils";
import { SnapLine } from "../rect-snapping";
import { rectWithPadding } from "../rect-utils";
import { combineHexWithOpacity } from "BEDROCK/utility/hex-utilities";
import { DrawDataConfig, drawData } from "./canvas-drawing-data";

export const drawBackdrops = ({
  map,
  canvasElement,
  project,
  pointerLocation,
  elementToHighlight,
  selectedElement,
  gpsSource,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const ctx = canvasElement.current.getContext("2d");
  // ctx.globalCompositeOperation = "difference";

  let backdropUnderCursor:
    | {
        backdrop: Backdrop;
        rect: Rect;
        dragHandleUnderCursor?: DragHandle;
        point: Point;
      }
    | undefined;
  [...project.backdrops].reverse().forEach((backdrop) => {
    const isSelected = selectedElement?.backdrop?.id === backdrop.id;
    const result = drawBackdrop({
      backdrop,
      ctx,
      pointerLocation,
      elementToHighlight,
      isSelected,
      gpsSource,
    });
    if (result.backdropUnderCursor) {
      backdropUnderCursor = result.backdropUnderCursor;
    }
  });
  if (backdropUnderCursor) {
    const mapElement: MapElement = {
      type: "backdrop",
      rect: backdropUnderCursor.rect,
      dragHandleUnderCursor: backdropUnderCursor.dragHandleUnderCursor,
      element: backdropUnderCursor.backdrop,
    };
    return {
      elementUnderPointerLocation: mapElement,
      snapLines: { vertical: undefined, horiztonal: undefined },
    };
  }
  return {
    elementUnderPointerLocation: undefined,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};

interface DrawBackdropConfig {
  backdrop: Backdrop;
  ctx: CanvasRenderingContext2D;
  pointerLocation: Point | undefined;
  elementToHighlight: MapElement | undefined;
  isSelected: boolean;
  gpsSource?: GPSSource;
  data?: DataVisualization;
}

const drawBackdrop = (
  config: DrawBackdropConfig
): {
  backdropUnderCursor:
    | {
        backdrop: Backdrop;
        dragHandleUnderCursor?: DragHandle;
        rect: Rect;
        point: Point;
      }
    | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const {
    backdrop,
    ctx,
    pointerLocation,
    elementToHighlight,
    isSelected,
    gpsSource,
  } = config;
  //backdrop.position is relative to the context/map size, e.g. 0.5, 0.5 is the center of the map
  // what is the actual screen size of the canvas?
  const borderWidth = backdrop.style.borderWidth;

  const x = backdrop.position.x * ctx.canvas.width + borderWidth / 2;
  const y = backdrop.position.y * ctx.canvas.height + borderWidth / 2;
  const width = backdrop.size.width * ctx.canvas.width - borderWidth;
  const height =
    backdrop.size.height * backdrop.size.width * ctx.canvas.width - borderWidth;

  const rect = { x, y, width, height };
  const isUnderCursor = pointerLocation
    ? pointIsInsideRect(pointerLocation, rect)
    : false;

  ctx.fillStyle = backdrop.style.backgroundColor;
  ctx.globalAlpha = backdrop.style.opacity;
  ctx.globalAlpha = 1;

  ctx.strokeStyle = backdrop.style.borderColor;
  ctx.lineWidth = borderWidth;

  ctx.lineJoin = backdrop.style.cornerRadius > 0 ? "round" : "miter";

  ctx.beginPath();
  ctx.moveTo(x + backdrop.style.cornerRadius, y);
  ctx.lineTo(x + width - backdrop.style.cornerRadius, y);
  ctx.arcTo(
    x + width,
    y,
    x + width,
    y + backdrop.style.cornerRadius,
    backdrop.style.cornerRadius
  );
  ctx.lineTo(x + width, y + height - backdrop.style.cornerRadius);
  ctx.arcTo(
    x + width,
    y + height,
    x + width - backdrop.style.cornerRadius,
    y + height,
    backdrop.style.cornerRadius
  );
  ctx.lineTo(x + backdrop.style.cornerRadius, y + height);
  ctx.arcTo(
    x,
    y + height,
    x,
    y + height - backdrop.style.cornerRadius,
    backdrop.style.cornerRadius
  );
  ctx.lineTo(x, y + backdrop.style.cornerRadius);
  ctx.arcTo(
    x,
    y,
    x + backdrop.style.cornerRadius,
    y,
    backdrop.style.cornerRadius
  );
  ctx.closePath();
  if (backdrop.style.borderWidth > 0) {
    ctx.stroke();
  }
  ctx.globalAlpha = backdrop.style.opacity;
  ctx.fill();
  ctx.globalAlpha = 1;

  if (gpsSource) {
    const config: DrawDataConfig = {
      ctx: ctx,
      rect,
      gpsSource: gpsSource,
      color: backdrop.style.backgroundColor,
    };
    drawData(config);
  }
  if (isSelected) {
    const paddedRect = rectWithPadding(rect, 5);
    drawSelectedRect(paddedRect, ctx);

    //Draw square drag handles 20px wide and tall, in top left and bottom right corner
    const handleSize = 20;
    ctx.fillStyle = "black";
    const leadingRect: Rect = {
      x: x - handleSize / 2,
      y: y - handleSize / 2,
      width: handleSize,
      height: handleSize,
    };
    ctx.fillRect(
      leadingRect.x,
      leadingRect.y,
      leadingRect.width,
      leadingRect.height
    );
    const trailingRect = {
      x: x + width - handleSize / 2,
      y: y + height - handleSize / 2,
      width: handleSize,
      height: handleSize,
    };
    ctx.fillRect(
      trailingRect.x,
      trailingRect.y,
      trailingRect.width,
      trailingRect.height
    );
    if (pointerLocation) {
      let dragHandleUnderPointer: DragHandle | undefined = undefined;
      if (pointIsInsideRect(pointerLocation, leadingRect)) {
        dragHandleUnderPointer = { position: "leading" };
      } else if (pointIsInsideRect(pointerLocation, trailingRect)) {
        dragHandleUnderPointer = { position: "trailing" };
      }
      const result = {
        backdropUnderCursor:
          dragHandleUnderPointer || isUnderCursor
            ? {
                backdrop,
                rect,
                point: { x, y },
                dragHandleUnderCursor: dragHandleUnderPointer,
              }
            : undefined,
        snapLines: { vertical: undefined, horiztonal: undefined },
      };

      return result;
    }
  }
  return {
    backdropUnderCursor: isUnderCursor
      ? { backdrop, rect, point: { x, y } }
      : undefined,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};
